/**
 * MarkdownModeTracer — a feature-gated, in-memory ring buffer that records the
 * markdown↔ADF conversion and sync decisions described in
 * `docs/devtools-tracing-design.md`. It answers "why did my content get
 * replaced / skipped / dropped / snapped back?" from the browser console (and,
 * later, from an external devtools panel).
 *
 * Shape mirrors `editor-ai-common`'s `AdfToolCallsDebugger`: a singleton gated
 * behind a feature flag, with an LRU + time-evicted buffer and a query API
 * attached to a `window` global.
 *
 * ## Two-level switch (zero cost when off)
 *
 *  1. **Gate `platform_editor_md_mode_tracer`** — the master switch. When off,
 *     `getInstance()` returns `null`, nothing attaches to `window`, and no
 *     `message` listener is installed. This is the GA / default state.
 *  2. **Recording enabled** — when the gate is on, recording is still opt-in so
 *     a broadly-enabled gate doesn't retain doc payloads for everyone. It
 *     defaults on only when the URL carries `?mdModeTracer=1`; a developer can
 *     also flip it live via `window.__markdownModeDebugger__.enable()`. No
 *     storage is touched (Browser Storage Consent safe), matching autocomplete's
 *     `debug-mode.ts`.
 *
 * Instrumentation sites MUST use the thunk form so nothing is built when off:
 *
 *   tracer?.recordSync(ctx, () => buildSyncInput());
 *
 * `recordSync` / `recordConvert` invoke the builder only when recording is
 * enabled, so neither the event object nor any `doc.toJSON()` is constructed in
 * the default state.
 *
 * Usage from the browser console (gate on):
 *   window.__markdownModeDebugger__.enable()
 *   window.__markdownModeDebugger__.getEvents({ kind: 'sync' })
 *   window.__markdownModeDebugger__.getLastSync()
 *   window.__markdownModeDebugger__.getTrace('md-12')
 */
import { bind } from 'bind-event-listener';

import { fg } from '@atlaskit/platform-feature-flags';

import type {
	ConvertEvent,
	ConvertTraceHook,
	ConvertTracePayload,
	MarkdownModeDebuggerApi,
	SyncEvent,
	SyncEventInput,
	TraceContext,
	TraceEvent,
	TraceEventFilter,
	TraceId,
	TracerClientMessage,
	TracerMessage,
	TraceSession,
	Trigger,
} from './md-mode-tracer-types';
import { TRACER_CLIENT_MESSAGE_SOURCE, TRACER_MESSAGE_SOURCE } from './md-mode-tracer-types';

declare global {
	interface Window {
		__markdownModeDebugger__: MarkdownModeDebuggerApi | undefined;
	}
}

const hasUrlEnableFlag = (): boolean => {
	if (typeof window === 'undefined') {
		return false;
	}
	try {
		return new URLSearchParams(window.location.search).get('mdModeTracer') === '1';
	} catch {
		return false;
	}
};

/** A round-trip probe registered by the active orchestrator (needs the editor schema). */
type RoundTripProbe = (input: string | object) => unknown;

export class MarkdownModeTracer {
	private static instance: MarkdownModeTracer | null = null;

	private events: TraceEvent[] = [];
	private idCounter = 0;
	private enabled: boolean;
	/**
	 * True when recording was switched on *because* a devtools client subscribed
	 * (rather than via the URL flag or a manual `enable()`). We restore the prior
	 * state when the last subscriber leaves, so the default-off privacy posture
	 * returns once nothing is watching.
	 */
	private autoEnabledForSubscribers = false;
	private probe: RoundTripProbe | null = null;
	private subscribers = new Set<WindowProxy>();
	private unbindMessageListener: (() => void) | null = null;

	/** Maximum number of events retained. */
	private readonly maxEvents: number;
	/** Maximum age of an event in milliseconds before eviction. */
	private readonly maxAgeMsec: number;

	private constructor(maxEvents = 200, maxAgeMsec = 30 * 60 * 1000) {
		this.maxEvents = maxEvents;
		this.maxAgeMsec = maxAgeMsec;
		this.enabled = hasUrlEnableFlag();
	}

	/**
	 * Returns the singleton if the gate is on, else `null`. On first creation it
	 * attaches the console API to `window.__markdownModeDebugger__` and installs
	 * the postMessage subscribe listener.
	 */
	static getInstance(): MarkdownModeTracer | null {
		if (!fg('platform_editor_md_mode_tracer')) {
			// Gate evaluated off. If an instance was created earlier in this session
			// (gate on, then flipped off via a Statsig refresh / bucket change), tear
			// it down so the console API, the message listener, the live subscribers,
			// and any retained doc payloads don't outlive the master switch.
			if (MarkdownModeTracer.instance) {
				MarkdownModeTracer.teardown();
			}
			return null;
		}
		if (!MarkdownModeTracer.instance) {
			MarkdownModeTracer.instance = new MarkdownModeTracer();
			MarkdownModeTracer.instance.attachToWindow();
			MarkdownModeTracer.instance.installMessageListener();
		}
		return MarkdownModeTracer.instance;
	}

	/**
	 * Opens a bound trace session for one top-level action. Returns `undefined`
	 * when the gate is off or recording is disabled, so a callsite's `trace?.`
	 * cheaply skips all instrumentation (no payload is ever built). This is the
	 * entry point instrumentation should use — it bundles `getInstance()` +
	 * `beginTrace()` and removes the need to thread both the tracer and the
	 * `TraceContext` through every record call.
	 */
	static begin(trigger: Trigger): TraceSession | undefined {
		const tracer = MarkdownModeTracer.getInstance();
		const ctx = tracer?.beginTrace(trigger);
		if (!tracer || !ctx) {
			return undefined;
		}
		return {
			id: ctx.id,
			// convertId always equals the trace id (same action), so fill it in here
			// and let an explicit value in build() override if ever needed.
			sync: (build) => tracer.recordSync(ctx, () => ({ convertId: ctx.id, ...build() })),
			convert: (build) => tracer.recordConvert(ctx, build),
			convertHook: () => tracer.convertHook(ctx),
		};
	}

	// --- Recording switch ---

	isEnabled(): boolean {
		return this.enabled;
	}

	enable(): void {
		this.enabled = true;
		// An explicit enable is sticky: don't let a later unsubscribe turn it off.
		this.autoEnabledForSubscribers = false;
	}

	disable(): void {
		this.enabled = false;
		this.autoEnabledForSubscribers = false;
	}

	/**
	 * Opens a trace context for one top-level action. Returns `undefined` when
	 * recording is disabled so callers cheaply skip all instrumentation.
	 */
	beginTrace(trigger: Trigger): TraceContext | undefined {
		if (!this.enabled) {
			return undefined;
		}
		return { id: `md-${++this.idCounter}`, trigger };
	}

	// --- Recording API (thunk form keeps zero cost when disabled) ---

	/**
	 * Record a conversion. `build` is invoked only when recording is enabled, so
	 * the captured mdast / ADF payloads are never constructed when off.
	 */
	recordConvert(ctx: TraceContext, build: () => ConvertTracePayload): void {
		if (!this.enabled) {
			return;
		}
		// Tracing is observe-only: a failure building or storing the event must
		// never propagate into the editor's conversion/sync path. Drop it instead.
		try {
			const payload = build();
			const event: ConvertEvent = {
				kind: 'convert',
				id: ctx.id,
				trigger: ctx.trigger,
				ts: Date.now(),
				...payload,
			};
			this.push(event);
		} catch {
			// Swallow — a tracer bug must not change editor behaviour.
		}
	}

	/**
	 * Binds a `TraceContext` into a `ConvertTraceHook` suitable for passing to
	 * `markdownToAdf` / `adfToMarkdown`. The conversion helper builds the payload
	 * (it knows the mdast/timing); this just stamps it with the context.
	 */
	convertHook(ctx: TraceContext): ConvertTraceHook {
		return (payload: ConvertTracePayload) => this.recordConvert(ctx, () => payload);
	}

	/** Record a sync decision. `build` is invoked only when recording is enabled. */
	recordSync(ctx: TraceContext, build: () => SyncEventInput): void {
		if (!this.enabled) {
			return;
		}
		// Observe-only: never let a builder/serialization/store failure escape into
		// the writeback path (a throw here would otherwise be caught by a caller's
		// catch and misread as a parse/rebase failure — see the conversion helpers).
		try {
			const input = build();
			const event: SyncEvent = {
				kind: 'sync',
				id: ctx.id,
				trigger: ctx.trigger,
				ts: Date.now(),
				...input,
				gates: input.gates ?? MarkdownModeTracer.snapshotGates(),
			};
			this.push(event);
		} catch {
			// Swallow — a tracer bug must not change editor behaviour.
		}
	}

	/**
	 * Snapshot the gates whose state is interesting when interpreting a sync
	 * decision. `fg()` must be called with a string literal (the
	 * static-feature-flags lint rule statically extracts flag keys), so each gate
	 * is read explicitly rather than looped. `fg` can throw when a gate isn't
	 * registered in this context, so each read is guarded and simply omitted.
	 */
	static snapshotGates(): Record<string, boolean> {
		const gates: Record<string, boolean> = {};
		const add = (key: string, read: () => boolean) => {
			try {
				gates[key] = read();
			} catch {
				// Gate not registered in this context — leave it out of the snapshot.
			}
		};
		add('platform_editor_md_source_sync_snapshot', () =>
			fg('platform_editor_md_source_sync_snapshot'),
		);
		add('platform_editor_markdown_compatible_toolbar', () =>
			fg('platform_editor_markdown_compatible_toolbar'),
		);
		add('platform_editor_md_mode_rebase_fallback', () =>
			fg('platform_editor_md_mode_rebase_fallback'),
		);
		add('platform_editor_mdast_handler_fallback', () =>
			fg('platform_editor_mdast_handler_fallback'),
		);
		add('platform_editor_md_mode_tracer', () => fg('platform_editor_md_mode_tracer'));
		return gates;
	}

	private push(event: TraceEvent): void {
		this.evictExpired();
		this.events.push(event);
		if (this.events.length > this.maxEvents) {
			this.events.splice(0, this.events.length - this.maxEvents);
		}
		this.emit(event, 'event');
	}

	private evictExpired(): void {
		const now = Date.now();
		const cutoff = now - this.maxAgeMsec;
		// Events are pushed in time order, so drop the expired prefix.
		let firstLive = 0;
		while (firstLive < this.events.length && this.events[firstLive].ts < cutoff) {
			firstLive++;
		}
		if (firstLive > 0) {
			this.events.splice(0, firstLive);
		}
	}

	// --- Query API (exposed on window) ---

	getEvents(filter?: TraceEventFilter): TraceEvent[] {
		this.evictExpired();
		let result = this.events.slice();
		if (filter?.kind) {
			result = result.filter((e) => e.kind === filter.kind);
		}
		if (filter?.trigger) {
			result = result.filter((e) => e.trigger === filter.trigger);
		}
		if (filter?.id) {
			result = result.filter((e) => e.id === filter.id);
		}
		if (filter?.decision) {
			result = result.filter((e) => e.kind === 'sync' && e.decision === filter.decision);
		}
		return result;
	}

	getLastSync(): SyncEvent | undefined {
		this.evictExpired();
		for (let i = this.events.length - 1; i >= 0; i--) {
			const event = this.events[i];
			if (event.kind === 'sync') {
				return event;
			}
		}
		return undefined;
	}

	getTrace(id: TraceId): TraceEvent[] {
		this.evictExpired();
		return this.events.filter((e) => e.id === id);
	}

	clear(): void {
		this.events = [];
	}

	/**
	 * Registers the active orchestrator's round-trip implementation (which holds
	 * the editor schema). `roundTrip()` delegates to it.
	 */
	registerProbe(probe: RoundTripProbe | null): void {
		this.probe = probe;
	}

	roundTrip(input: string | object): unknown {
		if (!this.probe) {
			return { error: 'No editor registered. Open markdown mode in an editor first.' };
		}
		try {
			return this.probe(input);
		} catch (err) {
			// The probe runs `markdownToAdf`, a "throws on parse error" boundary, so
			// malformed input would otherwise escape the console API as an uncaught
			// throw. Surface it as a structured result, matching the no-probe branch.
			return { error: err instanceof Error ? err.message : String(err) };
		}
	}

	// --- Layer 3 postMessage bridge (see design doc §5) ---

	private installMessageListener(): void {
		if (typeof window === 'undefined' || this.unbindMessageListener) {
			return;
		}
		const messageListener = (event: MessageEvent) => {
			if (event.origin !== window.location.origin) {
				return;
			}
			const data = event.data as TracerClientMessage | undefined;
			if (!data || data.source !== TRACER_CLIENT_MESSAGE_SOURCE) {
				return;
			}
			const source = event.source as WindowProxy | null;
			if (!source) {
				return;
			}
			if (data.type === 'subscribe') {
				const isNewSubscriber = !this.subscribers.has(source);
				this.subscribers.add(source);
				// Opening a devtools client is itself an explicit opt-in, so turn
				// recording on if it isn't already. We remember that we did so in
				// order to restore the prior state when the last client leaves.
				if (!this.enabled) {
					this.enabled = true;
					this.autoEnabledForSubscribers = true;
				}
				// Replay the current buffer only to a genuinely new subscriber. The
				// handshake (see design §6) tells an early-loading client to
				// re-subscribe on the `online` ping, so a duplicate `subscribe` from
				// an already-registered window must not replay the buffer twice.
				if (isNewSubscriber) {
					for (const buffered of this.events) {
						this.postTo(source, buffered, 'replay');
					}
				}
			} else if (data.type === 'unsubscribe') {
				this.subscribers.delete(source);
				this.restoreEnabledIfNoSubscribers();
			}
		};
		this.unbindMessageListener = bind(window, { type: 'message', listener: messageListener });
		// Announce that the tracer is now listening. A client that loaded before
		// the editor mounted (e.g. a content script injected at document_start)
		// will have sent its `subscribe` into the void; this prompts it to retry.
		this.announceOnline();
	}

	/** Broadcast a one-shot `online` ping so early-loading clients re-subscribe. */
	private announceOnline(): void {
		if (typeof window === 'undefined') {
			return;
		}
		const message: TracerMessage = { source: TRACER_MESSAGE_SOURCE, type: 'online' };
		try {
			window.postMessage(message, window.location.origin);
		} catch {
			// Ignore postMessage failures (e.g. opaque origin).
		}
	}

	/**
	 * When the last subscriber disconnects, undo a subscribe-driven auto-enable so
	 * recording returns to its default-off state once nothing is watching. A
	 * manual `enable()` clears the auto flag, so this never overrides the user.
	 */
	private restoreEnabledIfNoSubscribers(): void {
		if (this.subscribers.size === 0 && this.autoEnabledForSubscribers) {
			this.enabled = false;
			this.autoEnabledForSubscribers = false;
		}
	}

	private emit(event: TraceEvent, type: TracerMessage['type']): void {
		if (this.subscribers.size === 0) {
			return;
		}
		// Iterate a snapshot: a failed `postTo` deletes the dead subscriber from the
		// live Set (and may flip the recording switch off) mid-broadcast, so we must
		// not mutate the Set we are iterating.
		for (const subscriber of [...this.subscribers]) {
			this.postTo(subscriber, event, type);
		}
	}

	private postTo(subscriber: WindowProxy, event: TraceEvent, type: TracerMessage['type']): void {
		const message: TracerMessage = { source: TRACER_MESSAGE_SOURCE, type, event } as TracerMessage;
		try {
			subscriber.postMessage(message, window.location.origin);
		} catch {
			// Subscriber gone / cross-origin — drop it.
			this.subscribers.delete(subscriber);
			this.restoreEnabledIfNoSubscribers();
		}
	}

	private attachToWindow(): void {
		if (typeof window === 'undefined') {
			return;
		}
		try {
			window.__markdownModeDebugger__ = {
				getEvents: (filter?: TraceEventFilter) => this.getEvents(filter),
				getLastSync: () => this.getLastSync(),
				getTrace: (id: TraceId) => this.getTrace(id),
				roundTrip: (input: string | object) => this.roundTrip(input),
				enable: () => this.enable(),
				disable: () => this.disable(),
				isEnabled: () => this.isEnabled(),
				clear: () => this.clear(),
			};
		} catch {
			// Ignore window access errors.
		}
	}

	/**
	 * Fully dismantles the singleton: unbinds the message listener, drops all
	 * subscribers, clears the buffer and recording flags, removes the `window`
	 * global, and nulls the instance. Used both when the gate flips off after
	 * creation and by `resetForTesting`, so neither leaves the bridge live.
	 */
	private static teardown(): void {
		const instance = MarkdownModeTracer.instance;
		if (instance) {
			instance.unbindMessageListener?.();
			instance.unbindMessageListener = null;
			instance.subscribers.clear();
			instance.probe = null;
			instance.events = [];
			instance.enabled = false;
			instance.autoEnabledForSubscribers = false;
		}
		if (typeof window !== 'undefined') {
			window.__markdownModeDebugger__ = undefined;
		}
		MarkdownModeTracer.instance = null;
	}

	/**
	 * Resets the singleton. Test-only.
	 * @internal
	 */
	static resetForTesting(): void {
		MarkdownModeTracer.teardown();
	}
}
